import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpRequest,
  HttpContext,
  HttpContextToken,
} from '@angular/common/http';
import { Observable } from 'rxjs';

// Token to skip interceptors for S3 uploads
export const SKIP_INTERCEPTOR = new HttpContextToken<boolean>(() => false);
import config from '../../../../config.js';
import {
  BcrStatusDto,
  LegalEntityDetailDto,
  LegalEntityDto,
  CountryGroup,
  UploadStatus,
  DocumentType,
  UploadUrlRequest,
  PresignedPost,
  PresignedUploadResponse,
  ConfirmUploadResponse,
  DocumentFile,
  DownloadUrlResponse,
} from '../models/legal-entity.models';

// Re-export all models for backward compatibility
export type {
  BcrStatusDto,
  LegalEntityDetailDto,
  LegalEntityDto,
  CountryGroup,
  DocumentType,
  UploadUrlRequest,
  PresignedPost,
  PresignedUploadResponse,
  ConfirmUploadResponse,
  DocumentFile,
  DownloadUrlResponse,
};
export { UploadStatus };

@Injectable({ providedIn: 'root' })
export class LegalEntityService {
  private readonly base = config.API_GATEWAY_ENDPOINT?.replace(/\/$/, '') || '';
  // Fallback allows tests/local run if config not populated yet
  private readonly endpoint = `${this.base}/legal_entities`;

  constructor(private http: HttpClient) {}

  getLegalEntities(): Observable<CountryGroup[]> {
    return this.http.get<CountryGroup[]>(this.endpoint);
  }

  /**
   * Fetch the list of BCR status objects from the backend.
   */
  getBcrStatuses(): Observable<BcrStatusDto[]> {
    const base = this.base;
    const endpoint = `${base}/legal_entities/bcr_status`;
    return this.http.get<BcrStatusDto[]>(endpoint);
  }

  getLegalEntityDetail(entityId: number): Observable<LegalEntityDetailDto> {
    return this.http.get<LegalEntityDetailDto>(`${this.endpoint}/detail/${entityId}`);
  }

  /**
   * Check whether a user has permission for a given module/role.
   * Expects body: { prid_user, role_name: string[], module_name: string[] }
   */
  hasPermission(pridUser: string) {
    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: ['BCR - PST member'],
      module_name: ['Legal Entity List'],
    };
    return this.http.post<{ has_permission: boolean }>(endpoint, body);
  }

  /**
   * Request a change of BCR status for a legal entity.
   * Expects payload: { user_prid, legal_entity_id, bcr_status_id, link }
   */
  changeBcrStatus(userPrid: string, legalEntityId: number, bcrStatusId: number, link: string) {
    const endpoint = `${this.base}/legal_entities/change_bcr_status_request`;
    const body = {
      user_prid: userPrid,
      legal_entity_id: legalEntityId,
      bcr_status_id: bcrStatusId,
      link,
    };
    return this.http.put<{ success?: boolean; message?: string }>(endpoint, body);
  }

  /**
   * Approve or reject a BCR status change request.
   * Expects payload with all notification data + request_status: 'Approved' | 'Rejected'
   */
  updateBcrStatusRequest(
    id: number,
    author: string,
    approver: string,
    createdDate: number,
    requestStatus: 'Approved' | 'Rejected',
    legalEntityId: number,
    newBcrStatusId: number,
    previousBcrStatusId: number,
    message: string = '',
    lastUpdatedDate: number | null = null,
  ) {
    const endpoint = `${this.base}/legal_entities/change_bcr_status`;
    const body = {
      id,
      author,
      message,
      approver,
      created_date: createdDate,
      request_status: requestStatus,
      legal_entity_id: legalEntityId,
      last_updated_date: lastUpdatedDate,
      new_bcr_status_id: newBcrStatusId,
      previous_bcr_status_id: previousBcrStatusId,
    };
    return this.http.put<{ success?: boolean; message?: string }>(endpoint, body);
  }

  // ========== Document Management Methods ==========

  /**
   * Get all available document types (predefined templates)
   * Endpoint: GET /document_types
   */
  getDocumentTypes(): Observable<DocumentType[]> {
    return this.http.get<DocumentType[]>(`${this.base}/document_types`);
  }

  /**
   * Generate presigned upload URL for direct browser-to-S3 upload
   * Endpoint: POST /legal_entities/{id}/documents/upload-url
   *
   * @param legalEntityId - Legal entity ID
   * @param request - Upload request with document_type_name and optional comments
   * @returns Presigned POST URL data and document_file_id for confirmation
   */
  generateUploadUrl(
    legalEntityId: number,
    request: UploadUrlRequest,
  ): Observable<PresignedUploadResponse> {
    return this.http.post<PresignedUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/documents/upload-url`,
      request,
    );
  }

  /**
   * Upload file directly to S3 using presigned POST URL
   * This method constructs FormData with presigned fields and file
   *
   * CRITICAL: Fields must be appended BEFORE the file
   * CRITICAL: Must skip auth interceptor - S3 uses presigned URL authentication
   * CRITICAL: Content-Type must match the one used when generating presigned URL
   *
   * @param file - File to upload
   * @param presignedPost - Presigned POST data from generateUploadUrl
   * @returns Observable with upload progress events
   */
  uploadToS3(
    file: File,
    presignedPost: PresignedUploadResponse['presigned_post'],
  ): Observable<HttpEvent<unknown>> {
    const formData = new FormData();

    const fieldOrder = [
      'key',
      'bucket',
      'Policy',
      'X-Amz-Algorithm',
      'X-Amz-Credential',
      'X-Amz-Date',
      'X-Amz-Security-Token',
      'X-Amz-Signature',
      'Content-Type',
    ];

    fieldOrder.forEach((key) => {
      if (presignedPost.fields[key] !== undefined) {
        formData.append(key, presignedPost.fields[key]);
      }
    });

    // If Content-Type is not in presigned fields, add it manually based on file type
    if (!presignedPost.fields['Content-Type']) {
      formData.append('Content-Type', file.type || 'application/octet-stream');
    }

    // Append any remaining fields not in the order list
    Object.keys(presignedPost.fields).forEach((key) => {
      if (!fieldOrder.includes(key)) {
        const value = presignedPost.fields[key];
        if (value !== undefined) {
          formData.append(key, value);
        }
      }
    });
    formData.append('file', file, file.name);
    // Create request with progress tracking and SKIP interceptor
    // Do NOT set Content-Type header - let browser set it with boundary for multipart/form-data
    const req = new HttpRequest('POST', presignedPost.url, formData, {
      reportProgress: true,
      context: new HttpContext().set(SKIP_INTERCEPTOR, true),
    });

    return this.http.request(req);
  }

  /**
   * Confirm successful S3 upload and activate document record
   * Endpoint: POST /documents/{document_file_id}/confirm
   *
   * @param documentFileId - Document file ID from generateUploadUrl response
   * @returns Confirmation data with S3 metadata
   */
  confirmUpload(documentFileId: number): Observable<ConfirmUploadResponse> {
    return this.http.post<ConfirmUploadResponse>(
      `${this.base}/documents/${documentFileId}/confirm`,
      {},
    );
  }

  /**
   * Get all documents for a specific legal entity
   * Endpoint: GET /legal_entities/{id}/documents
   *
   * @param legalEntityId - Legal entity ID
   * @returns Array of document files with metadata
   */
  getLegalEntityDocuments(legalEntityId: number): Observable<DocumentFile[]> {
    return this.http.get<DocumentFile[]>(`${this.base}/legal_entities/${legalEntityId}/documents`);
  }

  /**
   * Generate presigned download URL for a document
   * Endpoint: GET /legal_entities/{id}/documents/{document_file_id}/download-url
   *
   * @param legalEntityId - Legal entity ID
   * @param documentFileId - Document file ID
   * @returns Presigned GET URL for downloading the file
   */
  generateDownloadUrl(
    legalEntityId: number,
    documentFileId: number,
  ): Observable<DownloadUrlResponse> {
    return this.http.get<DownloadUrlResponse>(
      `${this.base}/legal_entities/${legalEntityId}/documents/${documentFileId}/download-url`,
    );
  }

  /**
   * Trigger file download in browser using presigned URL
   * Creates a temporary anchor element and triggers download
   *
   * @param downloadUrl - Presigned download URL
   * @param filename - Original filename for download
   */
  downloadFile(downloadUrl: string, filename: string): void {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
